import { Navbar } from "@/components/Navbar";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileSignature, Lock, Star, Ban, Flag, BadgeCheck } from "lucide-react";

const PROTECTIONS = [
  { icon: Lock, title: "Money held in escrow", text: "A backer's funds are held, not handed over. They're only released when the backer confirms the deal actually happened. Flake on the deal and you don't get paid — the money is refunded to the backer." },
  { icon: BadgeCheck, title: "Vetted listers", text: "Anyone posting a deal is reviewed and can earn a Verified Lister badge. Backers can choose to stay anonymous, but the person asking for money is accountable." },
  { icon: FileSignature, title: "Signed agreements", text: "Before money moves, both sides sign an in-app agreement spelling out the amount, the split and the plan. It's a clear, on-the-record commitment." },
  { icon: Star, title: "Reputation that follows you", text: "Every completed deal builds a public trust score. A strong track record earns more funding; a broken one is visible to everyone who considers working with you." },
  { icon: Ban, title: "Strikes and bans", text: "Break your word and you get a strike on your record. Three strikes and you're banned from the platform — indefinitely. Deception isn't tolerated." },
  { icon: Flag, title: "Report & dispute", text: "See something off? Report it. The Manager reviews disputes and applies consequences, from refunds to permanent bans." },
];

export default function TrustPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-100">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-3">
          <ShieldCheck className="h-4 w-4" /> Trust & Safety
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight">How you're protected</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-base">
          Hustler's Hall is built on a simple idea: back real people chasing real deals, and split the profit — safely.
          Here's exactly how we keep it legit and hold people to their word.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {PROTECTIONS.map((p) => (
            <div key={p.title} className="rounded-2xl bg-[#121721] border border-white/10 p-6">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center mb-3">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="text-white font-bold">{p.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6">
          <h3 className="text-white font-bold mb-2">An honest word on risk</h3>
          <p className="text-sm text-slate-400 leading-relaxed">
            Every investment carries risk and no return is guaranteed. We do everything we can to limit that risk —
            escrow, vetting, agreements and real consequences — but you should only fund what you can afford to put to work.
            Deceptive or shady users are banned indefinitely.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Button data-testid="trust-browse-btn" onClick={() => navigate("/marketplace")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full h-12 px-8">
            Browse deals
          </Button>
          <Button data-testid="trust-post-btn" onClick={() => navigate("/post")} variant="outline" className="rounded-full h-12 px-8 border-white/20 bg-white/5 text-white hover:bg-white/10">
            Post a deal
          </Button>
        </div>
      </div>
    </div>
  );
}
